import type { NetworkNode, OrbitLayer } from "./types";

const CITIES = ["Detroit", "Berlin", "Lagos", "London", "Seoul", "São Paulo", "Johannesburg", "Accra", "Minneapolis", "Lisbon"];
const TEMPOS = ["112–118 BPM log-drum", "124–130 four-on-the-floor", "140 jungle break", "96 trap-bounce", "stem-split booth mix"];
const BEHAVIORS = [
  "live stem isolation as the default mix",
  "first-7-second skip as the A&R gate",
  "WhatsApp night clips before any DSP page",
  "community/pirate first play DSPs cannot score",
  "club 30–80Hz withheld from phone masters",
  "generated warm-ups with human headliners",
];
const KILLS = [
  "rooms stay on official promo pools only",
  "radio adds still decide the budget",
  "phone/spatial masters carry the same low end",
  "headliners billed as generated",
];
const PALETTE = ["#73e0d2", "#f0bd68", "#ca8cff", "#427d68", "#cf9464"];

function uid() {
  return globalThis.crypto?.randomUUID?.() ?? `p-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function pick<T>(arr: T[], n: number) {
  return arr[Math.abs(n) % arr.length];
}

function hash(s: string) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export function proceduralMutations(topic: string, depth: number, count = 4): NetworkNode[] {
  const h = hash(topic + ":" + depth + ":" + Date.now().toString(36));
  const year = 2026 + Math.min(12, Math.max(1, depth + 1));
  const roles: { layer: OrbitLayer; tag: string; claim: (i: number) => string }[] = [
    {
      layer: "inner",
      tag: "How it happens",
      claim: (i) =>
        `By ${year}, ${pick(CITIES, h + i)} rooms run ${pick(TEMPOS, h + i + 3)} as ${pick(BEHAVIORS, h + i + 1)}`,
    },
    {
      layer: "middle",
      tag: "Where it spreads",
      claim: (i) =>
        `${pick(CITIES, h + i + 5)} copies the ${pick(TEMPOS, h + i + 2)} pattern out of “${topic.slice(0, 42)}”`,
    },
    {
      layer: "outer",
      tag: "What follows",
      claim: (i) =>
        `${year + 1}: ${pick(BEHAVIORS, h + i + 7)} becomes the measurable default off this branch`,
    },
    {
      layer: "outer",
      tag: "What would kill it",
      claim: (i) => `This dies if ${pick(CITIES, h + i + 9)} ${pick(KILLS, h + i + 4)}`,
    },
  ];
  return roles.slice(0, count).map((r, i) => ({
    id: uid(),
    name: r.claim(i),
    metricLabel: String(year),
    orbitLayer: r.layer,
    colorHex: PALETTE[i % PALETTE.length],
    cluster: r.tag,
    signalLabel: String(year),
    why: `Procedural mutation of “${topic.slice(0, 80)}”.`,
    relation: r.tag,
  }));
}
